import React, { useMemo } from 'react';
import Banner from '../../../../../components/Banner';
import IconAiArrow from '../../../../../components/IconWrapper/arrow-ai';
import usePlatform from '../../../../../hooks/usePlatform';
import { getBannerOperationExtraAttr } from '../../../../../scripts/utils';
import './index.scss';
import '../../../../../components/AIP/aip-banner-wrap.scss';

type NoteTip = { value?: string; label?: string; img?: string };

type FeatureItem = {
  title: string;
  description: string;
};

const bannerBg = '/images/aip/Manufacture/manufacture-banner-bg.svg';
const manufactureSectionDiagramStandardization =
  '/images/aip/Manufacture/manufacture/production-standardization-overview.png';
const manufactureSectionDiagramFullProcess =
  '/images/aip/Manufacture/manufacture/production-full-process-overview.png';
const manufactureSectionDiagramExecution =
  '/images/aip/Manufacture/manufacture/production-execution-closure-overview.png';
const manufactureSectionDiagramMaterialCost =
  '/images/aip/Manufacture/manufacture/material-refinement-cost-overview.png';
const case01 = '/images/aip/Manufacture/case-01.png';

type FeatureSectionData = {
  title: string;
  subtitle: string;
  previewAlt: string;
  previewImage: string;
  items: FeatureItem[];
};

const featureSections: FeatureSectionData[] = [
  {
    title: '生产标准化体系',
    subtitle: '让生产从「人治」走向「可复制」',
    previewAlt: '生产标准化体系示意图',
    previewImage: manufactureSectionDiagramStandardization,
    items: [
      {
        title: 'BOM多版本与结构化管理',
        description:
          '支持BOM创建、变更、版本管理与历史追溯，确保不同批次、不同版本产品使用正确物料结构，减少错料与返工风险。'
      },
      {
        title: '工艺路线与工序资源绑定',
        description:
          '将工序顺序、资源（设备/人员）与工艺参数标准化，避免现场依赖经验判断，提高生产稳定性与一致性。'
      },
      {
        title: '替代料机制保障连续生产',
        description:
          '预设替代关系、比例与优先级，在物料短缺时自动匹配可用方案，减少停线与临时决策成本。'
      },
      {
        title: '工程数据集中管理',
        description:
          '统一管理物料、BOM、工艺数据及变更记录，确保生产、计划、采购使用同一数据口径，自动生成多维度报表，直观呈现运营状态。'
      }
    ]
  },
  {
    title: '生产全流程管控',
    subtitle: '让每一张订单都有节奏、有交付结果',
    previewAlt: '生产全流程管控示意图',
    previewImage: manufactureSectionDiagramFullProcess,
    items: [
      {
        title: '订单快速创建与下达',
        description:
          '批量创建生产订单，自动关联销售订单与BOM/工艺数据，一键下达至车间，减少人工录入与准备时间。'
      },
      {
        title: '订单进度实时可见',
        description:
          '可视化跟踪订单全生命周期，实时展示订单在各工序的执行状态与完成进度，避免「现场黑盒」。'
      },
      {
        title: '异常暴露与干预',
        description:
          '对延期、卡点、资源冲突等情况自动预警，并支持调整与跟踪，避免问题累积。'
      },
      {
        title: '订单智能协同',
        description:
          '支持按批次/产线拆分订单或合并同类订单，自动同步计划与物料需求，减少设备切换与资源浪费。'
      }
    ]
  },
  {
    title: '生产执行闭环',
    subtitle: '让生产人员实时感知生产现场',
    previewAlt: '生产执行闭环示意图',
    previewImage: manufactureSectionDiagramExecution,
    items: [
      {
        title: '报工实时采集',
        description:
          '支持工序级报工、批量报工与移动报工，实时采集工时、产量等数据，减少人工统计误差。'
      },
      {
        title: '异常在线闭环处理',
        description:
          '对设备故障、缺料、质量问题等进行线上报备、处理与记录，形成完整异常台账并自动同步，减少交付影响。'
      },
      {
        title: '产能负荷一目了然',
        description:
          '实时监控设备负荷与产能利用率，自动识别与分析瓶颈并推荐优化方案，辅助调整生产安排。'
      },
      {
        title: '执行数据沉淀与反馈',
        description:
          '将报工与执行数据用于后续计划与排产优化，提高整体生产效率；自动统计生产绩效联动薪酬体系，为人员管理提供数据支撑。'
      }
    ]
  },
  {
    title: '物料精细化与成本可控',
    subtitle: '让物料用得更准、成本看得更清',
    previewAlt: '物料精细化与成本可控示意图',
    previewImage: manufactureSectionDiagramMaterialCost,
    items: [
      {
        title: '精准领料管控',
        description:
          '基于生产订单与BOM自动计算物料需求，支持按单领料与倒冲模式，减少人工干预与误差。'
      },
      {
        title: '物料流向全追溯',
        description:
          '记录领料、退料、移库等全过程数据，支持库存核对与责任追溯，提升物料利用率与成本核算精准度。'
      },
      {
        title: '呆滞物料识别与复用',
        description:
          '自动识别呆滞物料并预警，推荐复用方案，减少物料积压与资金占用，助力物料精细化管控。'
      },
      {
        title: '精益成本分析与优化',
        description:
          '深入分析全流程成本构成，精准核算单产品、单批次成本，对比计划与实际差异，定位浪费环节并推荐优化方案，助力企业降本增效、提升盈利。'
      }
    ]
  }
];

function FeatureSection({ section }: { section: FeatureSectionData }) {
  return (
    <section className="section">
      <div className="container">
        <h2 className="section-title">{section.title}</h2>
        <p className="section-subtitle feature-subtitle">{section.subtitle}</p>
        <div className="feature-split">
          <div className="feature-list">
            {section.items.map((item, index) => (
              <article key={`${section.title}-${item.title}`} className="feature-item feature-item-static">
                <div className="feature-head">
                  <span className="feature-title">{item.title}</span>
                  <span className="feature-index">#{index + 1}</span>
                </div>
                <p className="feature-desc">{item.description}</p>
              </article>
            ))}
          </div>
          <div className="feature-preview">
            <div className="feature-placeholder">
              <img
                className="feature-preview-image"
                src={section.previewImage}
                alt={section.previewAlt}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Manufacture() {
  const { isPc } = usePlatform() || {};

  const bannerConfig = useMemo(() => {
    const src = bannerBg;
    const note =
      '围绕生产执行全过程，通过工程数据标准化、生产过程可视化与执行闭环管理，让每一张生产订单都可跟踪、可控制、可交付。';

    const noteTips: NoteTip[] = [
      { value: '+30%', label: '生产效率' },
      { value: '-15%', label: '物料浪费' },
      { value: '+28%', label: '订单交付率' }
    ];

    return {
      src,
      mobileSrc: bannerBg,
      title: '业务应用-生产',
      subheading: '',
      legend: '对每一张生产订单进行全流程精益管控',
      note,
      noteTips,
      forceShowNote: true,
      operation: [
        {
          text: '立即咨询',
          icon: <IconAiArrow />,
          extraAttr: getBannerOperationExtraAttr({
            jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' }
          })
        }
      ]
    };
  }, [isPc]);

  return (
    <div className="manufacture-page">
      <div className="aip-banner-wrap manufacture-banner">
        <Banner
          showAdvantage={false}
          covered
          useArrow={false}
          useDots={false}
          size="large"
          type={'BANNER_TYPE.CAROUSEL'}
          name={typeof bannerConfig.title === 'string' ? bannerConfig.title : undefined}
          height={isPc ? 700 : 600}
          config={bannerConfig as any}
        />
      </div>

      <main>
        {featureSections.map((section) => (
          <FeatureSection key={section.title} section={section} />
        ))}

        {/* 行业实践案例 - 暂时隐藏 */}
        {/* <section className="section" id="cases">
          <div className="container">
            <h2 className="section-title">行业实践案例</h2>
            <p className="section-subtitle feature-subtitle">数字化升级与生产订单精益实践</p>
            <div className="cases">
              <a className="case-link" href="https://terminus.io/case-detail/2009001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 01</span>
                  <div className="case-body">
                    <h3 className="case-title">三力士 中国橡胶V带制造企业龙头</h3>
                    <p className="case-desc">
                      以生产制造为核心，围绕订单全生命周期与执行闭环，推进工程数据标准化与现场可视化，助力供应链整体数字化升级。
                    </p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src={case01} alt="三力士案例配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
            </div>
          </div>
        </section> */}
      </main>
    </div>
  );
}
